import { useState } from "react";
import type { useIssues } from "../../controllers/useIssues";
import type { Sprint } from "../../models/project";

const SPRINT_STATUS_LABEL: Record<Sprint["status"], string> = {
  FUTURE: "예정", ACTIVE: "진행 중", CLOSED: "종료",
};

// Backlog (issues with no sprint) + sprint lifecycle (create/start/complete), moving issues into a
// sprint, and a simple burndown chart (table + inline bars) for the selected sprint.
export function BacklogView({ issues }: { issues: ReturnType<typeof useIssues> }) {
  const [creatingSprint, setCreatingSprint] = useState(false);
  const [burndownSprintId, setBurndownSprintId] = useState<string | null>(null);

  return (
    <div>
      {issues.error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {issues.error}
        </div>
      )}

      <div className="detail-body">
        <div className="detail-main">
          <h3>백로그 · {issues.backlogIssues.length}</h3>
          <table className="sdui-table">
            <thead><tr><th>키</th><th>요약</th><th>포인트</th><th data-align="end">스프린트로 이동</th></tr></thead>
            <tbody>
              {issues.backlogIssues.map((issue) => (
                <tr key={issue.id}>
                  <td className="mono">{issue.key}</td>
                  <td>{issue.summary}</td>
                  <td>{issue.storyPoints ?? "—"}</td>
                  <td data-align="end">
                    <select
                      className="sdui-input" style={{ padding: "6px 8px", width: "auto" }}
                      value=""
                      onChange={(e) => { if (e.target.value) void issues.moveToSprint(issue.id, e.target.value); }}
                    >
                      <option value="">스프린트 선택</option>
                      {issues.sprints.filter((s) => s.status !== "CLOSED").map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
            {issues.backlogIssues.length === 0 && <tfoot><tr><td colSpan={4} className="sdui-table-empty">백로그가 비어 있습니다.</td></tr></tfoot>}
          </table>
        </div>

        <aside className="aside">
          <div className="info-card">
            <h4>스프린트</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {issues.sprints.map((s) => (
                <div key={s.id} className="screen-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 500 }}>{s.name}</span>
                    <span className="badge ok">{SPRINT_STATUS_LABEL[s.status]}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {s.status === "FUTURE" && (
                      <button className="sdui-button sdui-button-secondary" onClick={() => void issues.startSprintNow(s.id)}>시작</button>
                    )}
                    {s.status === "ACTIVE" && (
                      <button className="sdui-button sdui-button-secondary" onClick={() => {
                        if (window.confirm("이 스프린트를 완료하시겠습니까?")) void issues.completeSprintNow(s.id);
                      }}>완료</button>
                    )}
                    <button className="sdui-button sdui-button-secondary" onClick={() => {
                      setBurndownSprintId(s.id);
                      void issues.loadBurndown(s.id);
                    }}>번다운</button>
                    {s.status === "FUTURE" && (
                      <button className="sdui-button sdui-button-secondary" onClick={() => {
                        if (window.confirm("이 스프린트를 삭제하시겠습니까?")) void issues.removeSprint(s.id);
                      }}>삭제</button>
                    )}
                  </div>
                </div>
              ))}
              {issues.sprints.length === 0 && <div className="sdui-list-empty">스프린트가 없습니다.</div>}
            </div>
            {!creatingSprint ? (
              <button className="btn primary" style={{ marginTop: 12 }} onClick={() => setCreatingSprint(true)}>＋ 새 스프린트</button>
            ) : (
              <CreateSprintForm issues={issues} onDone={() => setCreatingSprint(false)} />
            )}
          </div>

          {burndownSprintId && (
            <Burndown chart={issues.burndownBySprintId[burndownSprintId] ?? null} />
          )}
        </aside>
      </div>
    </div>
  );
}

function CreateSprintForm({ issues, onDone }: { issues: ReturnType<typeof useIssues>; onDone: () => void }) {
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const created = await issues.addSprint({ name, goal: goal || undefined });
      if (created) { setName(""); setGoal(""); onDone(); }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
      <label className="sdui-field">
        <span className="sdui-label">이름</span>
        <input className="sdui-input" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="sdui-field">
        <span className="sdui-label">목표 (선택)</span>
        <input className="sdui-input" value={goal} onChange={(e) => setGoal(e.target.value)} />
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="btn ghost" style={{ width: "auto" }} onClick={onDone}>취소</button>
        <button className="btn primary" style={{ width: "auto" }} disabled={!name.trim() || busy} onClick={submit}>
          {busy ? "생성 중…" : "생성"}
        </button>
      </div>
    </div>
  );
}

function Burndown({ chart }: { chart: import("../../models/project").BurndownChart | null }) {
  if (!chart) return <div className="info-card"><div className="center">불러오는 중…</div></div>;
  const max = Math.max(chart.totalPoints, 1);
  return (
    <div className="info-card">
      <h4>{chart.sprintName} · 번다운</h4>
      <table className="sdui-table">
        <thead><tr><th>날짜</th><th>남은 포인트</th><th>이상적</th></tr></thead>
        <tbody>
          {chart.points.map((p) => (
            <tr key={p.date}>
              <td>{p.date}</td>
              <td>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: `${Math.min(100, (p.remaining / max) * 100)}%`, height: 8, background: "var(--blue)", borderRadius: 4 }} />
                  <span>{p.remaining}</span>
                </div>
              </td>
              <td>{p.ideal}</td>
            </tr>
          ))}
        </tbody>
        {chart.points.length === 0 && <tfoot><tr><td colSpan={3} className="sdui-table-empty">데이터가 없습니다.</td></tr></tfoot>}
      </table>
    </div>
  );
}
